import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/cloudflare';
import {
  json,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigate,
} from '@remix-run/react';
import { Provider } from 'urql';
import { cssBundleHref } from '@remix-run/css-bundle';

import './tailwind.css';
import { environment, Permission } from './state/environment';
import { useClient } from './graphql-client';
import { TooltipProvider } from './components/ui/tooltip';
import { Toaster } from './components/ui/toaster';
import { jwtDecode } from 'jwt-decode';
import { views } from './state/views';
import { useEffect } from 'react';
import { initializeTheme } from './state/theme';
import { toFrontendRole } from './state/role';

export const links: LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=JetBrains+Mono:ital,wght@0,100..800;1,100..800&display=swap',
  },
  ...(cssBundleHref ? [{ rel: 'stylesheet', href: cssBundleHref }] : []),
];

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
  const { VOUCHERS_BASE_URL, API_URL } = context.cloudflare.env as {
    VOUCHERS_BASE_URL: string;
    API_URL: string;
  };

  const url = new URL(request.url);
  if (url.pathname === '/login') {
    return json({
      redirect: '',
      VOUCHERS_BASE_URL,
      API_URL,
      viewsAccessible: [],
      permissions: [],
      redirectURL: '',
      teamId: '',
      teamName: '',
      userRole: '',
      staffId: '',
    });
  }

  const cookie = request.headers.get('Cookie');

  if (cookie) {
    const nivaasCookie = cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith('nivaas'));

    // New JWT payload shape with team-based access
    type TokenPayload = {
      id: string;
      teamId: string;
      teamType: 'operator' | 'partner';
      role: string;
      workspaces: string[];
    };

    let nivaasCookieValue: TokenPayload | null = null;

    try {
      nivaasCookieValue = jwtDecode(
        nivaasCookie?.split('=').slice(1).join('') ?? '',
      ) as TokenPayload;
    } catch (e) {
      console.error(e);
      throw redirect('/login');
    }

    // Map backend role format to frontend view system roles
    const userRole = toFrontendRole(nivaasCookieValue.role);
    const userRoles = new Set([userRole]);
    const userIsAnAgent = nivaasCookieValue.teamType === 'partner';

    const permissions: Permission[] = [];
    // Operators and most roles can edit bookings (except reception, who can only view)
    const userCanVerboseEditBookings = !userRoles.has('reception') && !userIsAnAgent;
    if (userCanVerboseEditBookings) {
      permissions.push({
        verboseEditBookings: true,
      });
    }
    // Only superAdmin and admin can edit leads
    const canEditLeads = userRoles.has('superAdmin') || userRoles.has('admin');
    if (canEditLeads) {
      permissions.push({
        canEditLeads: true,
      });
    }
    // Add agent-specific permission (can create bookings but not edit/cancel)
    if (userIsAnAgent) {
      permissions.push({
        isAgent: true,
      });
    }

    const viewsAccessible = views.filter((view) =>
      view.roles.some((role) => userRoles.has(role)),
    );

    const response = await fetch(`${API_URL}/session/validate`, {
      headers: {
        Cookie: cookie,
      },
    });

    if (response.ok) {
      let redirectURL = '';
      // Agents should go to their dedicated dashboard
      if (userIsAnAgent && !url.pathname.startsWith('/dashboard/agent')) {
        redirectURL = '/dashboard/agent-dashboard';
      }

      if (url.pathname === '/') {
        redirectURL = userIsAnAgent
          ? '/dashboard/agent-dashboard'
          : '/dashboard/bookings';
      }

      return json({
        VOUCHERS_BASE_URL,
        API_URL,
        viewsAccessible,
        permissions,
        redirectURL,
        teamId: nivaasCookieValue.teamId,
        teamName: '', // Will be fetched client-side
        userRole,
        staffId: nivaasCookieValue.id,
      });
    }
  }

  return redirect('/login');
};

// Script to prevent flash of wrong theme
const themeScript = `
  (function() {
    const theme = localStorage.getItem('nivaas-theme') || 'system';
    let resolved = theme;
    if (theme === 'system') {
      resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    document.documentElement.classList.add(resolved);
  })();
`;

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <script dangerouslySetInnerHTML={{ __html: themeScript }} />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        <Toaster />
      </body>
    </html>
  );
}

export default function App() {
  const {
    API_URL,
    VOUCHERS_BASE_URL,
    viewsAccessible,
    permissions,
    redirectURL,
    teamId,
    teamName,
    userRole,
    staffId,
  } = useLoaderData<typeof loader>();

  const client = useClient();
  const navigate = useNavigate();

  // Initialize environment state in useEffect to avoid setting state during render
  useEffect(() => {
    if (environment.API_URL !== API_URL) {
      environment.API_URL = API_URL;
    }
    if (environment.VOUCHERS_BASE_URL !== VOUCHERS_BASE_URL) {
      environment.VOUCHERS_BASE_URL = VOUCHERS_BASE_URL;
    }
    environment.views = viewsAccessible;
    environment.permissions = permissions;
    environment.teamId = teamId;
    environment.teamName = teamName;
    environment.userRole = userRole;
    environment.staffId = staffId;
  }, [
    API_URL,
    VOUCHERS_BASE_URL,
    viewsAccessible,
    permissions,
    teamId,
    teamName,
    userRole,
    staffId,
  ]);

  // Initialize theme on mount
  useEffect(() => {
    initializeTheme();
  }, []);

  useEffect(() => {
    if (redirectURL) {
      navigate(redirectURL);
    }
  }, [navigate, redirectURL]);

  return (
    <TooltipProvider>
      <Provider value={client}>
        <Outlet />
      </Provider>
    </TooltipProvider>
  );
}
